/**
 * Hybrid Logical Clock (HLC) — gibt Events eine stabile, geräteübergreifende Reihenfolge,
 * OHNE auf synchrone Uhren angewiesen zu sein. Wichtig, weil bunt gemischte Geräte (Handys)
 * keine exakte Uhrzeit garantieren und im Offline-Betrieb sortierbar bleiben müssen.
 *
 * Ein Timestamp besteht aus (Wandzeit-Millis, Zähler, deviceId). Bei gleicher Wandzeit
 * wird der Zähler erhöht; deviceId bricht verbleibende Gleichstände deterministisch.
 */

import type { DeviceId } from "./ids.js";

export interface Hlc {
  /** Wandzeit in Millisekunden (Unix epoch). */
  wallMs: number;
  /** Logischer Zähler bei gleicher Wandzeit. */
  counter: number;
  /** Erzeugendes Gerät (Tiebreaker). */
  node: DeviceId;
}

/** Vergleicht zwei HLCs. < 0: a vor b, 0: gleich, > 0: a nach b. */
export function compareHlc(a: Hlc, b: Hlc): number {
  if (a.wallMs !== b.wallMs) return a.wallMs - b.wallMs;
  if (a.counter !== b.counter) return a.counter - b.counter;
  return a.node < b.node ? -1 : a.node > b.node ? 1 : 0;
}

/** Serialisiert eine HLC sortierbar als String (für DB-Sortierung / Sync). */
export function hlcToString(h: Hlc): string {
  // wallMs (13-stellig gepolstert) : counter (6-stellig) : node
  return `${String(h.wallMs).padStart(15, "0")}:${String(h.counter).padStart(6, "0")}:${h.node}`;
}

export function hlcFromString(s: string): Hlc {
  const [wall, counter, node] = s.split(":");
  if (!wall || counter === undefined || !node) {
    throw new Error(`Ungültiger HLC-String: ${s}`);
  }
  return { wallMs: Number(wall), counter: Number(counter), node: node as DeviceId };
}

/**
 * Erzeugt HLCs für ein Gerät. `now` wird injiziert (kein Date.now() im Modul),
 * damit die Logik testbar und deterministisch bleibt.
 */
export class HlcClock {
  private last: Hlc;

  constructor(node: DeviceId, now: number) {
    this.last = { wallMs: now, counter: 0, node };
  }

  /** Nächster lokaler Tick. */
  now(wallMs: number): Hlc {
    if (wallMs > this.last.wallMs) {
      this.last = { wallMs, counter: 0, node: this.last.node };
    } else {
      this.last = { wallMs: this.last.wallMs, counter: this.last.counter + 1, node: this.last.node };
    }
    return { ...this.last };
  }

  /** Beim Empfang eines fremden Events: eigene Uhr mit der des Senders zusammenführen. */
  receive(remote: Hlc, wallMs: number): Hlc {
    const maxWall = Math.max(wallMs, this.last.wallMs, remote.wallMs);
    let counter: number;
    if (maxWall === this.last.wallMs && maxWall === remote.wallMs) {
      counter = Math.max(this.last.counter, remote.counter) + 1;
    } else if (maxWall === this.last.wallMs) {
      counter = this.last.counter + 1;
    } else if (maxWall === remote.wallMs) {
      counter = remote.counter + 1;
    } else {
      counter = 0;
    }
    this.last = { wallMs: maxWall, counter, node: this.last.node };
    return { ...this.last };
  }
}
